import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import qs from 'qs';

import {
  FLOORPLAN_UUID_QUERY_PARAM,
  FLOORPLAN_VIEW,
  FOCUS_RESOURCE_QUERY_PARAM,
  RESOURCES_VIEW_QUERY_PARAM,
} from 'floorplan/pages/native-floorplan/const';
import { useCoworkerSheetNavigation } from './use-coworker-sheet-navigation.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { replace } from 'store/router/actions';
import { searchParams as SearchParams } from 'shared/types/search-params';
import { CoworkerResponse } from 'store/api-client/coworker-types';

/** A row can be jumped to only when the coworker has a booking on a placed resource that day. */
export const isJumpable = (coworker: CoworkerResponse): boolean => !!coworker.booking?.floorplanUuid;

/**
 * "View" on a coworker row (§C2): one navigation that closes the sheet, puts the map on the floor
 * the desk is on, and names the desk to focus — `useNativeFloorplanMap` picks the focus up from the
 * url and the canvas centres/zooms it. One `replace` rather than three, so the back button never
 * lands on an intermediate state (sheet closed but wrong floor).
 */
export const useCoworkerJump = (): ((coworker: CoworkerResponse) => void) => {
  const dispatch = useDispatch();
  const queryParams = useSearchParams<SearchParams>();
  const { basePathname } = useCoworkerSheetNavigation();

  return useCallback(
    (coworker: CoworkerResponse) => {
      const { booking } = coworker;

      if (!booking?.floorplanUuid) {
        return;
      }

      const nextParams = {
        ...queryParams,
        [RESOURCES_VIEW_QUERY_PARAM]: FLOORPLAN_VIEW,
        [FLOORPLAN_UUID_QUERY_PARAM]: booking.floorplanUuid,
        [FOCUS_RESOURCE_QUERY_PARAM]: booking.resourceUuid,
      };

      dispatch(replace(`${basePathname}?${qs.stringify(nextParams)}`));
    },
    [dispatch, queryParams, basePathname],
  );
};
